import { Archive, AtSign, CalendarClock, ChartPie, FilePen, FolderOpen, Inbox, LayoutGrid, PenLine, ScanText, Search } from "lucide-react";
import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router";
import { useAufgaben, useDokumente, useFristen } from "../api/abfragen";
import { antwortBeobachten, antwortLaeuft } from "../api/assistent";
import { abzeichenText } from "../welt/abzeichen";
import { heuteBerlin, offenLage } from "../welt/lage";
import { PersonAnsichtWurzel } from "../welt/personAnsicht";
import { TabPille } from "../welt/TabPille";
import { useReiterKompakt } from "../welt/reiterKompakt";
import { Zaehler } from "./Badge";
import { Band } from "./Band";
import { BetaLeiste } from "./BetaZeichen";
import { Briefgeist, BriefgeistAktiv, type IconTyp } from "./Briefgeist";
import { ProfilKopfzeile, ProfilRail } from "./Profil";
import { tastaturBeobachten } from "./tastatur";
import { herkunft } from "../seiten/archiv";
import { NutzungHinweis } from "../seiten/NutzungHinweis";
import { BetaBegruessung } from "../seiten/rueckmeldung/BetaBegruessung";

/** `reiter` und `reiterIcon`: Name und Symbol in der Leiste unten, wenn sie von Rail und Seitenleiste abweichen (FW1). */
type Eintrag = { zu: string; titel: string; icon: IconTyp; ende?: boolean; reiter?: string; reiterIcon?: IconTyp };

/** Hauptnavigation wie die Tab-Leiste der iOS-App (W22d-8, D-067; W82 E1 mit D-333): Posteingang · Akten · Erfassen ·
 *  Briefe · Gaist, in der Tab-Leiste, der Rail und der Seitenleiste gleich. In der Leiste unten heisst der Posteingang
 *  „Start“ mit eigenem Symbol (FW1, D-416: nur die Leiste unten aendert sich, Route und Kennungen bleiben). Der Gaist
 *  traegt den Briefgeist. Die Einstellungen liegen hinter dem Profil oben links (Profil.tsx), die Suche als Lupe oben im
 *  Posteingang bzw. in der Seitenleiste. */
const HAUPT: Eintrag[] = [
  { zu: "/", titel: "Posteingang", icon: Inbox, ende: true, reiter: "Start", reiterIcon: LayoutGrid },
  // W82-2 N3 (B9): in der Leiste unten die Symbole der Vorlage R11 (Archivkasten, Stift), Rail und Seitenleiste bleiben
  { zu: "/akten", titel: "Akten", icon: FolderOpen, reiterIcon: Archive },
  { zu: "/erfassen", titel: "Erfassen", icon: ScanText },
  { zu: "/briefe", titel: "Briefe", icon: FilePen, reiterIcon: PenLine },
  { zu: "/assistent", titel: "Gaist", icon: Briefgeist },
];
/** Weitere Bereiche nur in Rail und Seitenleiste; mobil als Zeilen in Akten (Finanzen), im Posteingang (Fristen-Modul
 *  mit „Alle“, Archiv) und in Erfassen und den Einstellungen (Post per Mail). „Blaettern“ heisst Archiv (Plan 6.10). */
const WEITERE: Eintrag[] = [
  { zu: "/fristen", titel: "Fristen", icon: CalendarClock },
  { zu: "/finanzen", titel: "Finanzen", icon: ChartPie },
  { zu: "/blaettern", titel: "Archiv", icon: Archive },
  { zu: "/post-per-mail", titel: "Post per Mail", icon: AtSign },
];

/** Zahlen des Abzeichens am Posteingang (plan-w82.md 3.6): zu pruefen plus ueberfaellig. W82 Web-5f (BS27, K36, D-584):
 *  überfällig zählt wie die Lage der Startseite (`offenLage`) Fristen und offene Aufgaben mit Fälligkeit; immer der ganze
 *  Haushalt, auch bei gewählter Personen-Ansicht (wie die Zahl zu prüfen). */
function useAbzeichen(): { pruefen: number; ueberfaellig: number } {
  const { data } = useDokumente({ status: "pruefen", pro_seite: 1 });
  const { data: fristen } = useFristen(true);
  const { data: aufgaben } = useAufgaben();
  const pruefen = data?.gesamt ?? 0;
  const { ueberfaellig } = offenLage({ fristen: fristen ?? [], aufgaben: aufgaben ?? [], pruefen: 0, fixkosten: null }, heuteBerlin());
  return { pruefen, ueberfaellig };
}

/** Zaehler der Leisten: nur am Posteingang, Zahl zu pruefen plus ueberfaellig, Lesetext „2 zu prüfen, 1 überfällig“ (Teile
 *  ohne Null, wie iOS); kein Zaehler an den Fristen. */
function useZaehler(abzeichen: { pruefen: number; ueberfaellig: number }): Record<string, { anzahl: number; label: string }> {
  return { "/": { anzahl: abzeichen.pruefen + abzeichen.ueberfaellig, label: abzeichenText(abzeichen) } };
}

/** Symbol eines Eintrags: am Gaist funkelt der Funke, solange eine Antwort entsteht. `wieReiter`: Rail und Seitenleiste
 *  tragen die Symbole der Leiste unten wie die Vorlage R11 „schreibtisch“ (W82 Teil 4, E25); die Namen bleiben (FW1). */
function useSymbol(): (e: Eintrag, wieReiter?: boolean) => IconTyp {
  const laeuft = useAntwortLaeuft();
  return (e, wieReiter) => (e.zu === "/assistent" && laeuft ? BriefgeistAktiv : wieReiter ? (e.reiterIcon ?? e.icon) : e.icon);
}

function useAntwortLaeuft(): boolean {
  return useSyncExternalStore(antwortBeobachten, antwortLaeuft, () => false);
}

/** App-Huelle: feste Tab-Leiste unten bis 743 px, ab 744 px die Rail, ab 1024 px die volle
 *  Seitenleiste (Handoff PgNav, Web; Umschaltung in bausteine.css, .leiste-rail/.leiste-voll).
 *  ⌘K bzw. Strg+K springt von ueberall in die Suche (Plan 6.4). */
export function Schale(props: { children: ReactNode }) {
  const abzeichen = useAbzeichen();
  const zaehler = useZaehler(abzeichen);
  const navigieren = useNavigate();
  useEffect(() => {
    function taste(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        navigieren("/suche");
      }
    }
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, [navigieren]);
  // W73c (D-283): Bildschirmtastatur, Tab-Leiste weg und Eingabe am sichtbaren Ausschnitt (tastatur.ts)
  useEffect(() => tastaturBeobachten(), []);
  // W82 Web-5f (H2): die Personen-Ansicht gilt für Menü am Kürzel und Startseite gemeinsam
  return (
    <PersonAnsichtWurzel>
    {/* W82 Web-Großfenster (G1, HW24): die Schale füllt das Fenster, die Leiste steht bei jeder Breite am linken Rand;
        jede Seite begrenzt ihren Inhalt selbst (Seite.tsx, mittig rechts der Leiste) */}
    <div className="md:flex">
      <Rail zaehler={zaehler} />
      <Seitenleiste zaehler={zaehler} />
      <div className="min-w-0 flex-1">
        <Band />
        {props.children}
      </div>
      {/* W82 Teil 4 (FW22 Regel 2, Vorlage N3 „Kante“): wo der Inhalt unter die Pille läuft, verblasst er im Grund; die
          Unschärfe liegt darunter und wirkt nur, wo die Engine sie zeichnet (welt/rest.css) */}
      <div className="tab-kante md:hidden" aria-hidden="true">
        <div className="tab-kante-unschaerfe" data-schwebend="" />
      </div>
      <TabLeiste abzeichen={abzeichen} />
      {/* W77 (D-292): einmal das Blatt zur Nutzungsstatistik fuer Bestandskonten */}
      <NutzungHinweis />
      {/* W82 Beta-W (F2 = A): einmal die Beta-Begrüßung für Beta-Haushalte */}
      <BetaBegruessung />
    </div>
    </PersonAnsichtWurzel>
  );
}

/** Seitenleiste (W22d-w4): neutral auf dem Grund mit Linie rechts. Oben das Profil (Kreis, Name,
 *  Haushalt und Plan), darunter das Suchfeld mit ⌘K, dann die Hauptnavigation und mit Abstand die
 *  weiteren Bereiche. Zeile H 44, Radius 10; aktiv: Karte mit feiner Linie, 600, Akzent-Strich links
 *  und Symbol im Akzent (nie nur Farbe). */
function Seitenleiste(props: { zaehler: ReturnType<typeof useZaehler> }) {
  const symbol = useSymbol();
  return (
    <nav
      aria-label="Bereiche"
      className="seitenleiste leiste-voll sticky top-0 h-dvh w-(--sidebar-w) shrink-0 flex-col gap-0.5 overflow-y-auto overscroll-contain bg-pg-sidebar-bg px-3 pt-4 pb-4 linie-rechts"
    >
      <ProfilKopfzeile />
      <Link
        to="/suche"
        className="seiten-suche mb-3 flex min-h-(--sidebar-row) shrink-0 items-center gap-2 rounded-md bg-pg-field-bg px-3 text-pg-text-3 shadow-(--pg-feld-rand) hover:text-pg-text-2"
        aria-keyshortcuts="Meta+K Control+K"
      >
        <Search size={20} strokeWidth={1.5} aria-hidden className="shrink-0" />
        <span className="t-footnote min-w-0 flex-1 text-pg-text-2">Suchen</span>
        <kbd className="t-caption-1 rounded-xs px-1 text-pg-text-2 shadow-(--pg-feld-rand)" aria-hidden>
          ⌘K
        </kbd>
      </Link>
      {HAUPT.map((b) => (
        <SeitenZeile key={b.zu} eintrag={b} icon={symbol(b, true)} zaehler={props.zaehler[b.zu]} />
      ))}
      <div className="mt-4 flex flex-col gap-0.5">
        {WEITERE.map((b) => (
          <SeitenZeile key={b.zu} eintrag={b} icon={b.icon} />
        ))}
      </div>
      {/* W82 Beta-W2 (D-701): das Zeichen „Beta“ als letztes Element unten */}
      <BetaLeiste form="seitenleiste" />
    </nav>
  );
}

/** Feste Trennstellen (weiches Trennzeichen) fuer lange Namen: bei grosser Schrift bricht die
 *  Seitenleiste „Post-eingang“ statt „Posteinga-ng“; Silbentrennung per Woerterbuch gibt es nicht in
 *  jedem Browser (Verifier W5, F9). */
const TRENNUNG: Record<string, string> = { Posteingang: "Post­eingang" };

/** Name mit fester Trennstelle: sichtbar mit, vorgelesen ohne (Sprachsteuerung, Verifier W5 Runde 2, O4). */
function Name(props: { titel: string }) {
  const getrennt = TRENNUNG[props.titel];
  if (!getrennt) return <>{props.titel}</>;
  return (
    <>
      <span aria-hidden="true">{getrennt}</span>
      <span className="sr-only">{props.titel}</span>
    </>
  );
}

/** Rail (Handoff PgNav isRail, 88 px): zwischen Tab-Leiste und voller Seitenleiste, also bei 744 bis
 *  1023 px und bei grosser Schrift. Oben das Profil (nur der Kreis), dann Suchen, die Hauptnavigation
 *  und die weiteren Bereiche als Symbol 24 mit kurzem Namen darunter (11 statt 10 px, Plan 8.1).
 *  Eintrag mindestens 48 hoch; aktiv: Karte mit feiner Linie und 600, das Symbol im Akzent (nie nur
 *  Farbe). Die Suche fehlt im Handoff; ohne sie gaebe es am Tablet ohne Tastatur keinen Weg dorthin
 *  (der Posteingang zeigt das Suchfeld erst unter 744 px). */
function Rail(props: { zaehler: ReturnType<typeof useZaehler> }) {
  const symbol = useSymbol();
  return (
    <nav
      aria-label="Bereiche"
      className="seitenleiste leiste-rail sticky top-0 h-dvh w-(--rail-w) shrink-0 flex-col gap-0.5 overflow-y-auto overscroll-contain bg-pg-sidebar-bg px-2 pt-4 pb-4 linie-rechts"
    >
      <ProfilRail />
      <RailEintrag eintrag={{ zu: "/suche", titel: "Suchen", icon: Search }} icon={Search} />
      {HAUPT.map((b) => (
        <RailEintrag key={b.zu} eintrag={b} icon={symbol(b, true)} zaehler={props.zaehler[b.zu]} />
      ))}
      <div className="mt-3 flex flex-col gap-0.5">
        {WEITERE.map((b) => (
          <RailEintrag key={b.zu} eintrag={b} icon={b.icon} />
        ))}
      </div>
      <BetaLeiste form="rail" />
    </nav>
  );
}

const RAIL_ZEILE = "relative flex shrink-0 min-h-(--pg-hit) flex-col items-center justify-center gap-0.5 rounded-md px-1 py-2 text-center";

function RailEintrag(props: { eintrag: Eintrag; icon: IconTyp; zaehler?: { anzahl: number; label: string } }) {
  const { zu, titel, ende } = props.eintrag;
  const Icon = props.icon;
  const suche = zu === "/suche";
  return (
    <NavLink
      to={zu}
      end={ende}
      aria-keyshortcuts={suche ? "Meta+K Control+K" : undefined}
      className={({ isActive }) =>
        `${RAIL_ZEILE} transition-colors duration-(--pg-dur-fast) ${isActive ? "bg-pg-surface font-semibold text-pg-text shadow-karte" : "font-medium text-pg-text-2 hover:bg-pg-surface-2 hover:text-pg-text"}`
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={24} strokeWidth={1.5} aria-hidden className={isActive ? "text-pg-accent-text" : ""} />
          <span className="t-caption-2 max-w-full hyphens-manual">
            <Name titel={titel} />
          </span>
          {/* nach dem Namen, damit er vorne steht („Posteingang, 1 zu pruefen“); sichtbar am Symbol */}
          {props.zaehler && (
            <span className="absolute top-1 left-1/2 ml-1.5">
              <Zaehler anzahl={props.zaehler.anzahl} label={props.zaehler.label} />
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function SeitenZeile(props: { eintrag: Eintrag; icon: IconTyp; zaehler?: { anzahl: number; label: string } }) {
  const { zu, titel, ende } = props.eintrag;
  const Icon = props.icon;
  return (
    <NavLink
      to={zu}
      end={ende}
      className={({ isActive }) =>
        // shrink-0: ist die Leiste hoeher als der Bildschirm (grosse Schrift, Tablet), scrollt sie,
        // statt die Zeilen zu stauchen (W7: ein Eintrag mehr)
        `seiten-zeile relative flex shrink-0 min-h-(--sidebar-row) items-center gap-3 rounded-md px-3 t-body transition-colors duration-(--pg-dur-fast) ${isActive ? "bg-pg-surface font-semibold text-pg-text shadow-karte" : "font-medium text-pg-text-2 hover:bg-pg-surface-2 hover:text-pg-text"}`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && <span aria-hidden className="aktiv-strich absolute inset-y-2 left-0 w-(--strich-aktiv) rounded-pill bg-pg-accent-text" />}
          <Icon size={20} strokeWidth={1.5} aria-hidden className={`shrink-0 ${isActive ? "text-pg-accent-text" : ""}`} />
          <span className="min-w-0 flex-1 py-1 hyphens-manual">
            <Name titel={titel} />
          </span>
          {props.zaehler && (
            <span className="shrink-0">
              <Zaehler anzahl={props.zaehler.anzahl} label={props.zaehler.label} />
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

/** Welcher Tab gilt als aktiv: Unterseiten gehoeren zu ihrem Bereich (Finanzen zu den Akten, B-053; Archiv, Suche und
 *  die Fristen zum Posteingang, dort sitzen Lupe, Archiv-Link und das Fristen-Modul mit „Alle“; das Archiv aus Akten zu
 *  Akten). Die Briefe sind ein eigener Reiter (W82, D-333). */
function aktiverTab(pfad: string, zustand?: unknown): string | undefined {
  // W73c (D-288 H1): das Archiv aus Akten heraus bleibt im Tab Akten (Herkunft im Link-State, archiv.ts)
  if (pfad.startsWith("/blaettern") && herkunft(zustand) === "/akten") return "/akten";
  if (pfad.startsWith("/finanzen")) return "/akten";
  if (pfad.startsWith("/blaettern") || pfad.startsWith("/suche") || pfad.startsWith("/kennenlernen") || pfad.startsWith("/fristen")) return "/";
  return HAUPT.find((b) => (b.ende ? pfad === b.zu : pfad.startsWith(b.zu)))?.zu;
}

/** Tab-Leiste mobil (W82 E2): die schwebende Glas-Pille mit Links, `aria-current` und den Namen vor dem Symbol im DOM;
 *  jeder Reiter mindestens so breit wie sein Name (W52b, `.tab-raster`), ab etwa 150 % Schrift nur Symbole. Das Abzeichen
 *  sitzt am Posteingang („Start“) und zaehlt zu pruefen plus ueberfaellig. Der Funke der Glyphe im Reiter „Gaist“ steht in
 *  `--pgw-funke` und funkelt schneller, solange eine Antwort entsteht (welt.css). */
function TabLeiste(props: { abzeichen: { pruefen: number; ueberfaellig: number } }) {
  const ort = useLocation();
  const aktiv = aktiverTab(ort.pathname, ort.state);
  const antwortet = useAntwortLaeuft();
  const zahl = props.abzeichen.pruefen + props.abzeichen.ueberfaellig;
  const { kompakt, voll } = useReiterKompakt();
  return (
    <TabPille
      label="Bereiche"
      className="tab-pille-schale md:hidden"
      kompakt={kompakt}
      onVoll={voll}
      aktiv={HAUPT.findIndex((b) => b.zu === aktiv)}
      reiter={HAUPT.map((b) => ({
        name: b.reiter ?? b.titel,
        symbol: b.reiterIcon ?? b.icon,
        zu: b.zu,
        gaist: b.zu === "/assistent",
        antwortet: b.zu === "/assistent" && antwortet,
        abzeichen: b.zu === "/" && zahl > 0 ? props.abzeichen : undefined,
      }))}
    />
  );
}
